import React, { FC, RefObject, useEffect, useMemo, useRef, useState } from "react";
import { ScrollView } from "react-native";
import { Spinner } from "@lookiero/aurora";
import { QueryStatus } from "@lookiero/messaging-react";
import {
  AnswersProvider,
  AnswersValidationProvider,
  FocusedInputProvider,
  ScreenProjection,
} from "@lookiero/sty-psp-quiz-style-profile-common-ui";
import { MESSAGING_CONTEXT_ID } from "../../../delivery/baseBootstrap";
import { useViewScreenBySlugAndCustomerId } from "../../../projection/screen/react/useViewScreenBySlugAndCustomerId";
import { useViewStyleProfileAnswersByCustomerId } from "../../../projection/styleProfileAnswers/react/useViewStyleProfileAnswersByCustomerId";
import { useTrackScreenModalPageView } from "../../../tracking/useTrackScreenModalPageView";
import { useTrackScreenPageView } from "../../../tracking/useTrackScreenPageView";
import { HostScreenOnSuccessFunction } from "../../components/organisms/question/components/hostScreenCta/HostScreenCta";
import { useStaticInfo } from "../../hooks/useStaticInfo";
import { style } from "./Screen.style";
import { QuestionForm } from "./components/questionForm/QuestionForm";

interface OnScreenLoadedFunctionArgs {
  readonly screen: ScreenProjection;
}
interface OnScreenLoadedFunction {
  (args: OnScreenLoadedFunctionArgs): void;
}

interface ScreenProps {
  readonly slug?: string;
  readonly onSuccess: HostScreenOnSuccessFunction;
  readonly scrollRef?: RefObject<ScrollView | null>;
  readonly onScreenLoaded: OnScreenLoadedFunction;
  readonly onScreenNotFound?: () => void;
}

const Screen: FC<ScreenProps> = ({ slug, onSuccess, scrollRef, onScreenLoaded, onScreenNotFound }) => {
  const {
    customer: { customerId, country, segment },
    androidInAppBrowser,
    tradename,
  } = useStaticInfo();

  const [answers] = useViewStyleProfileAnswersByCustomerId({ customerId });
  /**
   * In order to solve the "unmount" transition, this view will allways be rendered
   * under the path ":slug?", setting as visible or not the Modal.
   *
   * Mantaining a reference to the previous question will allow us to hide the Modal while
   * rendering the previous (invalidated) screen projection.
   */
  const [screen, screenStatus] = useViewScreenBySlugAndCustomerId({ slug, customerId, experiment: undefined });
  const screenRef = useRef(screen);
  screenRef.current = slug ? screen : screenRef.current;
  useEffect(() => {
    if (!screen) {
      return;
    }

    onScreenLoaded({ screen });
  }, [onScreenLoaded, screen]);

  useEffect(() => {
    if (slug && screenStatus === QueryStatus.SUCCESS && !screen) {
      onScreenNotFound?.();
    }
  }, [onScreenNotFound, screen, screenStatus, slug]);

  useTrackScreenPageView({
    country,
    segment,
    slug: screen?.metadata.slug,
    customerId,
    tradename,
  });
  const trackSizeGuideModalPageView = useTrackScreenModalPageView({
    country,
    segment,
    slug: "size",
    customerId,
    tradename,
  });
  const trackSilhouetteGuideModalPageView = useTrackScreenModalPageView({
    country,
    segment,
    slug: "silhouette",
    customerId,
    tradename,
  });

  const currentScreen = !slug || screenRef.current?.metadata.slug === slug ? screenRef.current : undefined;

  /**
   * Stable reference for the questions array. Passing a fresh `[currentScreen]` on every render
   * retriggers AnswersProvider's `[questions, answers]` effect, which re-runs setContextAnswers and
   * re-renders the whole question form — blocking the JS thread long enough to keep the previous
   * question on screen for several seconds (LK-44460).
   */
  const questions = useMemo(() => (currentScreen ? [currentScreen] : []), [currentScreen]);

  /**
   * Rendering a question's form is expensive (~500ms+ on native) and synchronous, so React cannot
   * commit the new question until it finishes — leaving the previous question painted on screen for
   * that whole window (LK-44460). Paint a spinner first when opening/switching to a new question so
   * the previous question is replaced immediately by a loader, then mount the form. The modal-close
   * transition (`!slug`) intentionally keeps showing the previous screen and is not gated.
   */
  const [paintedSlug, setPaintedSlug] = useState<string | undefined>(slug);
  useEffect(() => {
    setPaintedSlug(slug);
  }, [slug]);
  const showForm = !slug || paintedSlug === slug;

  return currentScreen && answers && showForm ? (
    <AnswersProvider answers={answers} questions={questions}>
      <AnswersValidationProvider contextId={MESSAGING_CONTEXT_ID} scrollRef={scrollRef}>
        <FocusedInputProvider enabled={androidInAppBrowser} scrollRef={scrollRef}>
          <QuestionForm
            androidInAppBrowser={androidInAppBrowser}
            country={country}
            question={currentScreen}
            segment={segment}
            tradename={tradename}
            onOpenSilhouetteGuide={trackSilhouetteGuideModalPageView}
            onOpenSizeGuide={trackSizeGuideModalPageView}
            onSuccess={onSuccess}
          />
        </FocusedInputProvider>
      </AnswersValidationProvider>
    </AnswersProvider>
  ) : (
    <Spinner style={style.spinner} testID="spinner" />
  );
};

export type { OnScreenLoadedFunction };
export { Screen };
